// ----------------------------------------------------------------------
// <copyright file="Izenda.ContextMenuStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable, action, computed } from 'mobx';
import AuthorizationConstants from 'logic/data/Izenda.AuthorizationConstants';
import LicenseCheckerConstants from 'logic/data/Izenda.LicenseCheckerConstants';
import localizer from 'share/localization/Izenda.Localizer';
import uiState from 'logic/Izenda.UiState';
import fetcher from 'fetchers/Izenda.SystemSettingFetcher';
import { filter, isNil, lowerFirst, find, placeholder } from 'lodash/fp';

const menuDefinition = [
  {
    key: 'setupAndLicense',
    icon: 'icon-key',
    forSystemAdmin: true,
    get title() {
      return localizer.getText('CONTEXT_MENU_LABEL_SYSTEM_DB_LICENSE');
    }
  },
  {
    key: 'tenantManagement',
    icon: 'icon-group',
    forSystemUser: true,
    forMultipleTenant: true,
    get title() {
      return localizer.getText('CONTEXT_MENU_LABEL_TENANT_SETUP');
    },
    permissionKeys: [
      AuthorizationConstants.PERMISSION_KEY.TenantSetupActionsCreate,
      AuthorizationConstants.PERMISSION_KEY.TenantSetupActionsEdit,
      AuthorizationConstants.PERMISSION_KEY.TenantSetupActionsDel,
      AuthorizationConstants.PERMISSION_KEY.TenantSetupPermissionsValue
    ],
    permissionOperator: AuthorizationConstants.PERMISSION_OPERATOR.Or
  },
  {
    key: 'dataSetup',
    icon: 'icon-datasetup',
    get title() {
      return localizer.getText('CONTEXT_MENU_LABEL_DATA_SETUP');
    },
    isDirty: false,
    permissionKeys: [
      AuthorizationConstants.PERMISSION_KEY.DataSetupDataModelValue,
      AuthorizationConstants.PERMISSION_KEY.DataSetupAdvancedSettingsCategory,
      AuthorizationConstants.PERMISSION_KEY.DataSetupAdvancedSettingsOthers
    ],
    permissionOperator: AuthorizationConstants.PERMISSION_OPERATOR.Or,
    items: [
      {
        key: 'connectionString',
        icon: 'icon-connectionstring',
        get title() {
          return localizer.getText('CONTEXT_MENU_LABEL_CONNECTION_STRING');
        },
        isDirty: false,
        permissionKeys: [AuthorizationConstants.PERMISSION_KEY.DataSetupDataConnectorsValue]
      },
      {
        key: 'dataModel',
        icon: 'icon-datamodel',
        get title() {
          return localizer.getText('CONTEXT_MENU_LABEL_DATA_MODEL');
        },
        isDirty: false,
        needConnectionString: true,
        isDisabled: false,
        permissionKeys: [AuthorizationConstants.PERMISSION_KEY.DataSetupDataModelValue]
      },
      {
        key: 'advanceSettings',
        icon: 'icon-advancesetting',
        get title() {
          return localizer.getText('CONTEXT_MENU_LABEL_ADVANCED_SETTINGS');
        },
        isDisabled: false,
        permissionKeys: function () {
          return function () {
            // Check user is a system user.
            if (uiState.currentUser.systemAdmin) return true;

            // check others
            return this.checkPrivileges(
              [
                AuthorizationConstants.PERMISSION_KEY.DataSetupAdvancedSettingsCategory,
                AuthorizationConstants.PERMISSION_KEY.DataSetupAdvancedSettingsOthers
              ],
              AuthorizationConstants.PERMISSION_OPERATOR.Or
            );
          };
        }
      },
      {
        key: 'databaseMapping',
        icon: 'icon-mapping',
        get title() {
          return localizer.getText('CONTEXT_MENU_LABEL_DATABASE_MAPPING');
        },
        isDisabled: false,
        forSystemAdmin: true,
        forMultipleTenant: true
      },
      {
        key: 'import',
        icon: 'icon-import',
        get title() {
          return localizer.getText('CONTEXT_MENU_LABEL_IMPORT');
        },
        isDisable: false,
        permissionKeys: [AuthorizationConstants.PERMISSION_KEY.DataSetupImportValue]
      },
      {
        key: 'importHistory',
        icon: 'icon-import-history',
        get title() {
          return localizer.getText('CONTEXT_MENU_LABEL_IMPORT_HISTORY');
        },
        isDisable: false,
        permissionKeys: [AuthorizationConstants.PERMISSION_KEY.DataSetupImportValue]
      }
    ]
  },
  {
    key: 'roleSetup',
    icon: 'icon-group',
    get title() {
      return localizer.getText('CONTEXT_MENU_LABEL_ROLE_SETUP');
    },
    permissionKeys: [
      AuthorizationConstants.PERMISSION_KEY.RoleSetupActionsCreate,
      AuthorizationConstants.PERMISSION_KEY.RoleSetupActionsEdit,
      AuthorizationConstants.PERMISSION_KEY.RoleSetupActionsDel,
      AuthorizationConstants.PERMISSION_KEY.RoleSetupDataModelAccessValue,
      AuthorizationConstants.PERMISSION_KEY.RoleSetupPermissionsValue,
      AuthorizationConstants.PERMISSION_KEY.RoleSetupGrantRoleWithFullReportAndDashboardAccessValue
    ],
    permissionOperator: AuthorizationConstants.PERMISSION_OPERATOR.Or
  },
  {
    key: 'userSetup',
    icon: 'icon-useradmin',
    get title() {
      return localizer.getText('CONTEXT_MENU_LABEL_USER_SETUP');
    },
    permissionKeys: [
      AuthorizationConstants.PERMISSION_KEY.UserSetupActionsCreate,
      AuthorizationConstants.PERMISSION_KEY.UserSetupActionsEdit,
      AuthorizationConstants.PERMISSION_KEY.UserSetupActionsDel,
      AuthorizationConstants.PERMISSION_KEY.UserSetupActionsConfigureSecurityOptions,
      AuthorizationConstants.PERMISSION_KEY.UserSetupUserRoleAssociationValue
    ],
    permissionOperator: AuthorizationConstants.PERMISSION_OPERATOR.Or
  },
  {
    key: 'copyManagement',
    icon: 'icon-copy-mng',
    isDisabled: false,
    forSystemAdmin: true,
    forMultipleTenant: true,
    get title() {
      return localizer.getText('CONTEXT_MENU_LABEL_COPY_MANAGEMENT');
    }
  },
  {
    key: 'systemConfig',
    icon: 'icon-systemconfig',
    get title() {
      return localizer.getText('CONTEXT_MENU_LABEL_SYSTEM_CONFIGURATION');
    },
    permissionKeys: [AuthorizationConstants.PERMISSION_KEY.SystemConfigurationScheduledInstancesValue],
    items: [
      {
        key: 'securityPolicies',
        icon: 'icon-lock',
        get title() {
          return localizer.getText('CONTEXT_MENU_LABEL_SECURITY_POLICIES');
        },
        forSystemAdmin: true
      },
      {
        key: 'machineLearningDetails',
        icon: 'icon-setting',
        get title() {
          return localizer.getText('CONTEXT_MENU_LABEL_MACHINE_LEARNING');
        },
        licenseKey: LicenseCheckerConstants.LICENSE_KEY.MachineLearning,
        forSystemAdmin: true
      },
      {
        key: 'nlqConfiguration',
        icon: 'icon-setting',
        get title() {
          return localizer.getText('CONTEXT_MENU_LABEL_NLQ_CONFIG');
        },
        licenseKey: LicenseCheckerConstants.LICENSE_KEY.Nlq,
        forSystemAdmin: true
      },
      {
        key: 'emailConfiguration',
        icon: 'icon-mail',
        get title() {
          return localizer.getText('CONTEXT_MENU_LABEL_EMAIL');
        },
        forSystemAdmin: true
      },
      {
        key: 'reportConfiguration',
        icon: 'icon-report',
        get title() {
          return localizer.getText('CONTEXT_MENU_LABEL_REPORT');
        },
        forSystemAdmin: true
      },
      {
        key: 'scheduling',
        icon: 'icon-calendar',
        get title() {
          return localizer.getText('CONTEXT_MENU_LABEL_SCHEDULING');
        },
        permissionKeys: [AuthorizationConstants.PERMISSION_KEY.SystemConfigurationScheduledInstancesValue],
        licenseKey: LicenseCheckerConstants.LICENSE_KEY.Scheduling
      },
      {
        key: 'cachingConfiguration',
        icon: 'icon-cache',
        get title() {
          return localizer.getText('CONTEXT_MENU_LABEL_CACHE');
        },
        forSystemAdmin: true
      },
      {
        key: 'googleMapConfiguration',
        icon: 'icon-googlemap',
        get title() {
          return localizer.getText('CONTEXT_MENU_LABEL_GOOGLE_MAP');
        },
        forSystemAdmin: true
      },
      {
        key: 'exportingConfiguration',
        icon: 'icon-export',
        get title() {
          return localizer.getText('CONTEXT_MENU_LABEL_EXPORTING');
        },
        forSystemAdmin: true
      }
    ]
  }
];

export default class ContextMenuStore {
  @observable contextMenuActiveLink = 'setupAndLicense';
  @observable activeLink = null;
  @observable menuStatus = true;
  @observable contextMenu = menuDefinition;

  @computed
  get flattenContextMenu() {
    return placeholder(this.contextMenu)
      .flatMap(item => {
        const tempArray = isNil(item.items) ? [] : item.items.toJSON();
        tempArray.push(item);
        return tempArray;
      })
      .flattenDeep();
  }

  @computed
  get isCurrentActiveLinkDisable() {
    return !!this.flattenContextMenu.findLast({
      key: this.contextMenuActiveLink
    }).isDisabled;
  }

  @action
  changeActiveLink(newLink) {
    this.contextMenuActiveLink = this.activeLink = newLink;
  }

  /**
   * load system indicator for detecting changes
   */
  loadSystemIndicator() {
    const updateMenuDefinition = result => {
      const dataSetupMenu = find({key: 'dataSetup'}, this.contextMenu);
      const DIRTY_STATUS = '2';

      // Reset dirty state for 'dataSetup' menu item and its childs
      dataSetupMenu.isDirty = false;
      dataSetupMenu.items.forEach(item => {
        item.isDirty = false;
      });

      if (!isNil(find({value: DIRTY_STATUS}, result))) {
        dataSetupMenu.isDirty = true;
        filter({value: DIRTY_STATUS}, result).forEach(item => {
          const dbKey = lowerFirst(item.key);
          find({key: dbKey}, dataSetupMenu.items).isDirty = true;
        });
      }
    };

    return fetcher
      .getSystemIndicator(uiState.getTenantId())
      .then(updateMenuDefinition);
  }

  /**
   * disabled some for setting menu item if visible connetion == 0
   * @return {Promise} for unit test to make sure testing after complete set
   */
  disabledLinkBasedOnConnectionString() {
    const needUpdateList = this.flattenContextMenu.filter('needConnectionString');
    const updateMenuDefinition = result => {
      needUpdateList.forEach(item => item.isDisabled = result === 0);
      return result;
    };
    const resetMenuDefinition = () => {
      needUpdateList.forEach(item => item.isDisabled = true);
      return 0;
    };

    return fetcher
      .getNumberOfConnection(uiState.getTenantId())
      .then(updateMenuDefinition, resetMenuDefinition);
  }

  /**
   * Get Context Menu Store
   */
  getHelpSystemUrl() {
    return fetcher.getHelpSystemUrl();
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ContextMenuStore.js